import type { TenantRole } from '@dosfilos/domain';

// ─────────────────────────────────────────────────────────────────────────────
// permissions.config.ts — Fuente única de verdad para RBAC en el frontend
//
// Importar en:
//  - use-permission.ts (acciones CRUD por recurso)
//  - use-permissions.ts (rutas accesibles por rol)
//  - users.page.tsx (visualización en la UI)
// ─────────────────────────────────────────────────────────────────────────────

// ─── Módulos (recursos del sistema) ──────────────────────────────────────────

export type Resource =
  | 'people'
  | 'admissions'
  | 'academic'
  | 'grades'
  | 'crm'
  | 'sections'
  | 'formTemplates'
  | 'tuition'
  | 'users'
  | 'settings'
  | 'scholarships'
  | 'reports'
  | 'announcements'
  | 'courseMaterials'
  | 'cap'
  | 'membership';

export type Action = 'read' | 'create' | 'update' | 'delete' | 'close' | 'export';

/** i18n keys for module labels — resolved with t() at render (sis ns) */
export const MODULE_LABEL_KEYS: Record<Resource, string> = {
  people: 'usersPage.moduleLabels.people',
  admissions: 'usersPage.moduleLabels.admissions',
  academic: 'usersPage.moduleLabels.academic',
  sections: 'usersPage.moduleLabels.sections',
  grades: 'usersPage.moduleLabels.grades',
  crm: 'usersPage.moduleLabels.crm',
  formTemplates: 'usersPage.moduleLabels.formTemplates',
  tuition: 'usersPage.moduleLabels.tuition',
  users: 'usersPage.moduleLabels.users',
  settings: 'usersPage.moduleLabels.settings',
  scholarships: 'usersPage.moduleLabels.scholarships',
  reports: 'usersPage.moduleLabels.reports',
  announcements: 'usersPage.moduleLabels.announcements',
  courseMaterials: 'usersPage.moduleLabels.courseMaterials',
  cap: 'usersPage.moduleLabels.cap',
  membership: 'usersPage.moduleLabels.membership',
};

// ─── Matriz de permisos CRUD ──────────────────────────────────────────────────
// resource → action → roles autorizados
// 'tenantAdmin' siempre tiene bypass completo (ver hooks)

export const PERMISSION_MAP: Record<Resource, Partial<Record<Action, TenantRole[]>>> = {
  // S0·SEC-1b: el directorio completo de personas es de admin, registro,
  // decano, finanzas y revisor (decisión de Ricardo, 2026-10-22). El docente ve
  // a los alumnos de SUS secciones y el mentor a sus mentoreados, siempre por
  // el servidor —nunca el directorio—.
  people: {
    read: ['tenantAdmin', 'registrar', 'academicDean', 'finance', 'reviewer'],
    create: ['tenantAdmin', 'registrar'],
    update: ['tenantAdmin', 'registrar'],
    export: ['tenantAdmin', 'registrar'],
  },
  admissions: {
    read: ['tenantAdmin', 'registrar', 'reviewer'],
    create: ['tenantAdmin', 'registrar'],
    update: ['tenantAdmin', 'registrar'],
    export: ['tenantAdmin', 'registrar'],
  },
  // `academicDean` ESCRIBE ACÁ, y este mapa decía que no. El servidor exige
  // tenantAdmin | registrar | academicDean para toda la estructura académica
  // (`SECTION_MANAGEMENT_ROLES`); este mapa hoy no lo usa ninguna pantalla
  // —`usePermission` no tiene consumidores— pero una tabla de permisos que
  // afirma lo contrario del servidor es peor que no tenerla: el día que
  // alguien la conecte, deja fuera al decano sin que nadie entienda por qué.
  academic: {
    read: ['tenantAdmin', 'registrar', 'faculty', 'student'],
    create: ['tenantAdmin', 'registrar', 'academicDean'],
    update: ['tenantAdmin', 'registrar', 'academicDean'],
  },
  sections: {
    read: ['tenantAdmin', 'registrar', 'faculty', 'mentor'],
    create: ['tenantAdmin', 'registrar', 'academicDean'],
    update: ['tenantAdmin', 'registrar', 'academicDean'],
  },
  grades: {
    read: ['tenantAdmin', 'registrar', 'faculty', 'student'],
    create: ['tenantAdmin', 'registrar', 'faculty'],
    update: ['tenantAdmin', 'registrar', 'faculty'],
    close: ['tenantAdmin', 'registrar'],
    export: ['tenantAdmin', 'registrar'],
  },
  // S0·SEC-1b: el mentor sale del CRM (PM, 2026-10-22): las campañas leen el
  // directorio completo y no tenía otro uso legítimo ahí.
  crm: {
    read: ['tenantAdmin', 'registrar'],
    create: ['tenantAdmin', 'registrar'],
    update: ['tenantAdmin', 'registrar'],
  },
  formTemplates: {
    read: ['tenantAdmin', 'registrar'],
    create: ['tenantAdmin', 'registrar'],
    update: ['tenantAdmin', 'registrar'],
    delete: ['tenantAdmin'],
  },
  tuition: {
    read: ['tenantAdmin', 'finance', 'registrar'],
    create: ['tenantAdmin', 'finance', 'registrar'],
    update: ['tenantAdmin', 'finance'],
    export: ['tenantAdmin', 'finance'],
  },
  users: {
    read: ['tenantAdmin'],
    create: ['tenantAdmin'],
    update: ['tenantAdmin'],
    delete: ['tenantAdmin'],
  },
  settings: {
    read: ['tenantAdmin'],
    update: ['tenantAdmin'],
  },
  scholarships: {
    read: ['tenantAdmin', 'registrar', 'reviewer'],
    create: ['tenantAdmin', 'registrar'],
    update: ['tenantAdmin', 'registrar', 'reviewer'],
  },
  reports: {
    read: ['tenantAdmin', 'registrar', 'finance'],
    export: ['tenantAdmin', 'registrar', 'finance'],
  },
  announcements: {
    read: ['tenantAdmin', 'registrar'],
    create: ['tenantAdmin', 'registrar'],
    update: ['tenantAdmin', 'registrar'],
    delete: ['tenantAdmin', 'registrar'],
  },
  courseMaterials: {
    read: ['tenantAdmin', 'registrar', 'faculty', 'mentor', 'student'],
    create: ['tenantAdmin', 'registrar', 'faculty'],
    update: ['tenantAdmin', 'registrar', 'faculty'],
    delete: ['tenantAdmin', 'registrar', 'faculty'],
  },
  // Phase E — CAP compliance & certification. Faculty write is tenant-wide for
  // now (per-course ownership pending — see DECISIONS 2026-07-24).
  cap: {
    read: ['tenantAdmin', 'registrar', 'faculty', 'mentor'],
    update: ['tenantAdmin', 'registrar', 'faculty'],
  },
  // Camino TMAI — institutional requirements (R0). Managed by admin/registrar.
  membership: {
    read: ['tenantAdmin', 'registrar'],
    create: ['tenantAdmin', 'registrar'],
    update: ['tenantAdmin', 'registrar'],
  },
};

// ─── Rutas accesibles por rol ─────────────────────────────────────────────────
// Determina qué ítems del menú lateral se muestran a cada rol
// '*' → acceso completo

/** Todo rol que puede aparecer en una membership, más el rol de plataforma.
 *  Ambos mapas de abajo son EXHAUSTIVOS sobre este tipo a propósito. */
export type RoutableRole = TenantRole | 'platformAdmin';

/** Rutas por rol. EXHAUSTIVO sobre `RoutableRole`: agregar un rol al dominio NO
 *  COMPILA hasta asignarle rutas aquí.
 *
 *  No es ceremonia de tipos. `networkAuditor` se agregó en R1-PR1 sin entrada en
 *  este mapa ni en `ROLE_DEFAULT_ROUTE`; el resultado en producción fue que el
 *  revisor de TMAI entró a la app y aterrizó en `/admissions` — una pantalla de
 *  seminario, vacía, dentro del tenant de la red. Con `canAccess` fail-closed
 *  (ver use-permissions.ts) un rol huérfano ya no muestra la pantalla equivocada:
 *  deja al usuario sin NINGUNA. Que el compilador lo exija es la única forma de
 *  que no vuelva a nacer un rol sin destino. */
export const ROLE_PATH_MAP: Record<RoutableRole, string[]> = {
  tenantAdmin: ['*'],
  platformAdmin: ['*'],
  registrar: [
    '/admissions',
    '/cap',
    '/people',
    '/students',
    '/form-templates',
    '/academic',
    '/sections',
    '/grades',
    '/tuition',
    '/crm',
    '/scholarships',
    '/reportes',
    '/anuncios',
    '/materiales',
    '/curso',
    '/plantillas-syllabus',
    '/catalogo-libros',
    '/assignments',
    '/estandares',
    '/dialogo-socratico',
    '/practica-progreso',
    '/encuestas',
  ],
  // Autoridad académica: currículo y acreditación. Deliberadamente SIN admisiones,
  // aranceles ni CRM — no es un registrar con más permisos, es otro oficio. El
  // catálogo (`/academic`), las plantillas y el módulo CAP son su territorio.
  academicDean: [
    '/cap',
    '/people',
    '/students',
    '/academic',
    '/sections',
    '/plantillas-syllabus',
    '/catalogo-libros',
    '/curso',
    '/materiales',
    '/estandares',
    // Calificar en SOLO LECTURA (decisión de Ricardo, 2026-10-30): el decano
    // lee las entregas y las notas, como ya le permite el servidor; corregir
    // sigue siendo del docente y del registro (GRADING_STAFF_ROLES).
    '/assignments',
  ],
  reviewer: ['/admissions', '/people', '/scholarships'],
  // S0·SEC-1b: sin `/people`. El docente ve a sus alumnos en la nómina de su
  // sección (/curso, /sections), no en el directorio del seminario.
  faculty: [
    '/cap',
    '/academic',
    '/sections',
    '/grades',
    '/materiales',
    '/curso',
    '/plantillas-syllabus',
    '/catalogo-libros',
    '/assignments',
    '/estandares',
    '/dialogo-socratico',
    '/practica-progreso',
    '/encuestas',
  ],
  // S0·SEC-1b: sin `/people` ni `/crm`. Ver arriba (faculty) y `crm` en
  // PERMISSION_MAP. PR 3A: sin `/grades` — `getSectionGradebook` no le da el
  // libro de notas a quien no enseña, y la pantalla le mostraba sólo el error.
  // En /curso la pestaña Notas también se le esconde (curso-tabs).
  mentor: [
    '/academic',
    '/sections',
    '/materiales',
    '/curso',
    '/plantillas-syllabus',
    '/catalogo-libros',
    '/assignments',
  ],
  finance: ['/admissions', '/people', '/tuition', '/reportes'],
  student: ['/portal', '/portal-v2'],
  // El revisor de una red (TMAI) NO ve nada del seminario: su único destino es
  // el espacio de la red. El panel real es R1-PR6; hoy `/red` es una pantalla
  // estática que anuncia que está en preparación.
  networkAuditor: ['/red'],
  // `donor` existe en el dominio y todavía no tiene portal. Sin rutas: entra al
  // sistema y no accede a nada, que es la verdad — mejor que colarlo en una
  // pantalla de staff. Cuando exista el portal de donantes, va acá.
  donor: [],
};

/** ¿Alguno de estos cargos entra a esta ruta? Por PREFIJO: `/academic` abre
 *  también `/academic/anuncios`. La misma regla para el menú y para las
 *  guardas de ruta (`usePermissions().canAccess`), y para los tests. */
export function rolesCanAccess(roles: readonly string[], path: string): boolean {
  return roles.some((role) => {
    const allowed = ROLE_PATH_MAP[role as RoutableRole] ?? [];
    return allowed.includes('*') || allowed.some((p) => path.startsWith(p));
  });
}

// ─── Guardas de las rutas que NO se protegen con su propio camino ───────────
// Una ruta se guarda, por defecto, con su propio camino. Estas no: o su
// prefijo dejaría pasar a quien no corresponde, o el servidor admite otros
// cargos que los del prefijo. Viven acá —no sueltas en App.tsx— para que el
// menú pregunte por LA MISMA ruta que la guarda, y para que un test compare
// cada una con lo que acepta el servidor.
export const ROUTE_GUARDS = {
  /** `/academic/anuncios`: publicar anuncios es de Registro (y admins). Por el
   *  prefijo `/academic` el menú se lo mostraba a decano, docente y mentor, y
   *  la guarda los devolvía a su inicio. */
  announcementsAdmin: '/anuncios',
  /** `/students/bulk-enroll`: el servidor (`bulkEnrollStudents`,
   *  `resendBulkWelcomeEmail`) admite admin, registro y decano — los cargos con
   *  `/students`. Con `/sections` la abrían también docente y mentor. */
  bulkEnroll: '/students',
  /** `/assignments/quiz/new` y `/:id/edit`: `createQuiz` admite al docente de la
   *  sección y a admin, registro y decano — el decano tiene `/curso`, no
   *  `/assignments`. */
  quizBuilder: '/curso',
  /** `/assignments/:id/grade`: `gradeSubmission` admite admin, registro y
   *  docente. El decano ENTRA en solo lectura (ve entregas y notas, sin
   *  herramientas de corrección); el servidor le sigue negando corregir. */
  gradeAssignment: '/assignments',
  /** `/sections/configure/:termId`: configurar un período es gestión
   *  (`SECTION_MANAGEMENT_ROLES`: admin, registro y decano — los cargos con
   *  `/students`). Con `/sections` la abrían docente y mentor, y la página
   *  carga el plantel docente completo desde el cliente (S0·SEC-1b). */
  configureTerm: '/students',
} as const;

/** Adónde se recarga al cambiar de seminario. NO a una página fija: el cargo
 *  en el seminario nuevo todavía no se conoce, y `/reportes` hacía rebotar a
 *  docente, mentor, revisor y decano. `/login` con sesión iniciada espera la
 *  membresía nueva y lleva al inicio de ESE cargo (`LoginOrRedirect`). */
export const AFTER_TENANT_SWITCH = '/login';

/** ¿Puede abrirse el portal del alumno? Solo con el cargo de alumno, o con una
 *  vista «Ver portal» abierta por el personal. Cualquier otra sesión veía su
 *  portal «propio», vacío o sin persona vinculada. */
export function portalEntryAllowed(input: { roles: readonly string[]; viewing: boolean }): boolean {
  return input.viewing || input.roles.includes('student');
}

// ─── Ruta por defecto al hacer login ─────────────────────────────────────────

/** Dónde aterriza cada rol. EXHAUSTIVO sobre `RoutableRole` por la misma razón
 *  que `ROLE_PATH_MAP`: un rol sin destino manda al usuario al final de
 *  `getDefaultRoute()`, que hoy es `/admissions`. */
export const ROLE_DEFAULT_ROUTE: Record<RoutableRole, string> = {
  // Roles with reports access land on the dashboard (Reportes e Indicadores).
  // The rest land on their daily desk. Must stay in sync with ROLE_PATH_MAP so
  // a role is never redirected to a page it can't open (use-permissions.spec).
  tenantAdmin: '/reportes',
  platformAdmin: '/reportes',
  registrar: '/reportes',
  academicDean: '/academic',
  finance: '/reportes',
  reviewer: '/people',
  // S0·SEC-1b: ni el docente ni el mentor tienen ya el directorio. El docente
  // aterriza en su escritorio de curso; el mentor, en el catálogo académico
  // (no tiene datos de alumnos fuera de su mentoría).
  faculty: '/curso',
  mentor: '/academic',
  student: '/portal-v2',
  networkAuditor: '/red',
  // Sin portal propio todavía: aterriza donde el guard le va a decir que no hay
  // nada para él, en vez de en una pantalla de staff que no le corresponde.
  donor: '/no-access',
};

/** Orden de precedencia cuando alguien tiene varios roles: gana el primero que
 *  aparezca acá. `networkAuditor` va PRIMERO — si un usuario tuviera ese rol y
 *  otro, el de la red manda: nunca queremos que un revisor caiga por defecto en
 *  una pantalla de seminario. */
export const ROLE_PRIORITY_ORDER = [
  'networkAuditor',
  'tenantAdmin',
  'platformAdmin',
  'registrar',
  // Después del registrar: quien tiene los dos roles entra por la vista más
  // amplia, y el catálogo de Secretaría es superconjunto del del decano.
  'academicDean',
  'reviewer',
  'faculty',
  'mentor',
  'finance',
  'student',
  'donor',
] as const;

// ─── Metadata de roles para la UI ────────────────────────────────────────────

export interface RoleMeta {
  value: TenantRole;
  /** i18n key for the role label — resolved with t() at render (sis ns) */
  labelKey: string;
  /** i18n key for the role description — resolved with t() at render (sis ns) */
  descriptionKey: string;
  /** Módulos a los que da acceso de lectura (para mostrar en la UI) */
  modules: Resource[];
}

export const ALL_ROLES: RoleMeta[] = [
  {
    value: 'tenantAdmin',
    labelKey: 'usersPage.roleMeta.tenantAdmin.label',
    descriptionKey: 'usersPage.roleMeta.tenantAdmin.description',
    modules: [
      'people',
      'admissions',
      'academic',
      'sections',
      'grades',
      'crm',
      'formTemplates',
      'tuition',
      'users',
      'settings',
      'announcements',
      'courseMaterials',
    ],
  },
  {
    value: 'registrar',
    labelKey: 'usersPage.roleMeta.registrar.label',
    descriptionKey: 'usersPage.roleMeta.registrar.description',
    modules: [
      'people',
      'admissions',
      'academic',
      'sections',
      'grades',
      'crm',
      'formTemplates',
      'tuition',
      'announcements',
      'courseMaterials',
    ],
  },
  {
    value: 'academicDean',
    labelKey: 'usersPage.roleMeta.academicDean.label',
    descriptionKey: 'usersPage.roleMeta.academicDean.description',
    modules: ['people', 'academic', 'sections', 'grades', 'courseMaterials'],
  },
  {
    value: 'faculty',
    labelKey: 'usersPage.roleMeta.faculty.label',
    descriptionKey: 'usersPage.roleMeta.faculty.description',
    modules: ['academic', 'sections', 'grades', 'courseMaterials'],
  },
  {
    value: 'reviewer',
    labelKey: 'usersPage.roleMeta.reviewer.label',
    descriptionKey: 'usersPage.roleMeta.reviewer.description',
    modules: ['admissions'],
  },
  {
    value: 'mentor',
    labelKey: 'usersPage.roleMeta.mentor.label',
    descriptionKey: 'usersPage.roleMeta.mentor.description',
    modules: ['academic', 'sections', 'courseMaterials'],
  },
  {
    value: 'finance',
    labelKey: 'usersPage.roleMeta.finance.label',
    descriptionKey: 'usersPage.roleMeta.finance.description',
    modules: ['people', 'admissions', 'tuition'],
  },
  {
    value: 'student',
    labelKey: 'usersPage.roleMeta.student.label',
    descriptionKey: 'usersPage.roleMeta.student.description',
    modules: ['academic', 'grades', 'courseMaterials'],
  },
];
